import { useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { useAuthStore } from '@store/authStore';

/**
 * Normalize `bid_options.aircraft_features` to the scoped {beds, wifi,
 * entertainment} shape the BE column expects. Source options fetched via
 * `include=…` carry the joined platform-catalog aircraft_features row
 * instead (aircraft_feature_id, tier, custom_features, timestamps, …)
 * which the BE 500s on. Mirrors normalizeAircraftFeatures in
 * useQuoteProcessor.js.
 */
const AIRCRAFT_FEATURES_DEFAULT = { beds: 1, wifi: false, entertainment: false };
const normalizeAircraftFeatures = (features) => {
  if (!features || typeof features !== 'object') return { ...AIRCRAFT_FEATURES_DEFAULT };
  return {
    beds: Number.isFinite(features.beds) ? features.beds : AIRCRAFT_FEATURES_DEFAULT.beds,
    wifi: typeof features.wifi === 'boolean' ? features.wifi : AIRCRAFT_FEATURES_DEFAULT.wifi,
    entertainment: typeof features.entertainment === 'boolean' ? features.entertainment : AIRCRAFT_FEATURES_DEFAULT.entertainment,
  };
};

/**
 * Map a source bid_option to the shape the nested-write endpoint expects
 * for the duplicated bid. Every id is stripped (fresh INSERT), per-quote
 * selection state is reset (`is_recommended: false`, `status: 'DRAFT'`,
 * `shortlisted: true`) so downstream selectors don't collapse the new
 * quote's multi-tail view to whatever the source client had accepted.
 */
const mapOptionForDuplicate = (srcOpt) => ({
  aircraft_id: srcOpt?.aircraft_id || '',
  operator_id: srcOpt?.operator_id || '',
  base_price: Number(srcOpt?.base_price || 0).toFixed(2),
  fuel_cost: Number(srcOpt?.fuel_cost || 0).toFixed(2),
  landing_fees: Number(srcOpt?.landing_fees || 0).toFixed(2),
  crew_fees: Number(srcOpt?.crew_fees || 0).toFixed(2),
  catering_cost: Number(srcOpt?.catering_cost || 0).toFixed(2),
  ground_transport: Number(srcOpt?.ground_transport || 0).toFixed(2),
  other_fees: Number(srcOpt?.other_fees || 0).toFixed(2),
  total_price: Number(srcOpt?.total_price || 0).toFixed(2),
  broker_markup: Number(srcOpt?.broker_markup || 0).toFixed(2),
  final_price: Number(srcOpt?.final_price || 0).toFixed(2),
  federal_excise_tax: Number(srcOpt?.federal_excise_tax || 0).toFixed(2),
  segment_tax: Number(srcOpt?.segment_tax || 0).toFixed(2),
  is_international: !!srcOpt?.is_international,
  international_tax: Number(srcOpt?.international_tax || 0).toFixed(2),
  platform_fee: Number(srcOpt?.platform_fee || 0).toFixed(2),
  operator_cost: Number(srcOpt?.operator_cost || 0).toFixed(2),
  // Per-option accept/select state is INTENTIONALLY reset — the
  // duplicated quote is a fresh negotiation. Carrying `status: 'ACCEPTED'`
  // or `shortlisted: false` from the source made downstream selectors
  // (selectAcceptedBidOption in QuoteCard / QuoteDetailsDrawer) collapse
  // the new quote's multi-tail view to just the one option the client
  // had accepted on the source. Force every carried-over option back to
  // a live-candidate DRAFT so the new quote behaves like an unselected
  // fresh multi-tail quote.
  is_recommended: false,
  recommendation_reason: srcOpt?.recommendation_reason || '',
  availability_confirmed: srcOpt?.availability_confirmed !== false,
  shortlisted: true,
  risk_level: srcOpt?.risk_level || 'LOW',
  trust_score: Number(srcOpt?.trust_score || 0).toFixed(2),
  estimated_flight_time: srcOpt?.estimated_flight_time || 0,
  aircraft_features: normalizeAircraftFeatures(srcOpt?.aircraft_features),
  // Duplicated options are freshly attached to the new quote — status
  // 'SENT' matches every other create/attach path in the app. The
  // source option's status (which may be ACCEPTED / REJECTED / SENT
  // from the original negotiation) is intentionally dropped: the
  // duplicate is a fresh negotiation with no prior client input.
  status: 'SENT',
});

/**
 * useQuoteDuplicator
 *
 * Exposes `duplicateQuote(source)` which deep-clones an existing quote into
 * a brand-new DRAFT with fresh server IDs and `valid_until` reset to now +
 * 7 days. The source quote is untouched. `quote_number` is intentionally
 * NOT FE-generated — BE mints it server-side.
 *
 * Uses the atomic nested-write endpoint `POST /quotes-with-bids`, so the
 * whole duplication is one round trip: quote + bids + bid_options +
 * passengers + files → single transaction.
 *
 * Returns `{ duplicateQuote, isDuplicating, currentStep }`. `currentStep`
 * is retained for compat with the progress UI, though the nested endpoint
 * completes in one step.
 */
export function useQuoteDuplicator() {
  const queryClient = useQueryClient();
  const { userProfile } = useAuthStore();

  const [isDuplicating, setIsDuplicating] = useState(false);
  const [currentStep, setCurrentStep] = useState(null);

  const duplicateQuote = useCallback(async (source) => {
    if (!source) {
      return { success: false, error: new Error('No source quote provided') };
    }
    if (!userProfile?.userprofile_id || !userProfile?.org_id) {
      return { success: false, error: new Error('Current user is missing — sign in and retry') };
    }

    setIsDuplicating(true);
    setCurrentStep('Duplicating quote…');

    const validUntil = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

    try {
      const sourceBidItems = Array.isArray(source.bid_items) ? source.bid_items : [];

      // Build the bids array — one entry per source bid, all fresh
      // inserts (no bid_id → INSERT). Options nest inside each bid.
      // trip_number gets a per-index suffix so multi-bid duplicates
      // don't collide on the T{rand} column.
      const randTrip = Math.floor(Math.random() * 1000000);
      const bids = sourceBidItems
        .map((bi, i) => {
          const srcBid = bi?.bid;
          if (!srcBid) return null;
          // Skip soft-deleted options (`shortlisted: false`) — they represent
          // tails a prior broker removed from the source quote. The source
          // bid still carries the DB row (BE has no hard-delete for
          // bid_options), so if we don't filter here every duplicated
          // quote resurrects them. Same rationale as loadFromQuote's
          // hydration filter and the client-preview / email filters
          // shipped earlier.
          const srcOptions = (Array.isArray(srcBid.bid_options) ? srcBid.bid_options : [])
            .filter((o) => o?.shortlisted !== false);
          return {
            trip_number: `T${randTrip}${i}`,
            created_by: userProfile.userprofile_id,
            broker_id: userProfile.userprofile_id,
            broker_org_id: userProfile.org_id,
            client_name: srcBid.client_name || '',
            client_email: srcBid.client_email || '',
            client_phone: srcBid.client_phone || '',
            departure_airport: srcBid.departure_airport || '',
            arrival_airport: srcBid.arrival_airport || '',
            departure_date: srcBid.departure_date || null,
            return_date: srcBid.return_date || null,
            passenger_count: srcBid.passenger_count || 1,
            baggage_requirements: srcBid.baggage_requirements || '',
            special_requests: srcBid.special_requests || '',
            // Flip straight to SENT so the duplicated bids don't linger
            // in the shortlist drawer's DRAFT-filtered query. Mirrors
            // the create-flow behaviour where every path SENT-flips its
            // bid immediately.
            status: 'SENT',
            total_amount: Number(srcBid.total_amount || 0).toFixed(2),
            currency: srcBid.currency || 'USD',
            valid_until: validUntil,
            sent_at: null,
            viewed_at: null,
            accepted_at: null,
            notes: srcBid.notes || '',
            message_id: null,
            leg_itinerary: Array.isArray(srcBid.leg_itinerary) ? srcBid.leg_itinerary : [],
            bid_options: srcOptions.map(mapOptionForDuplicate),
          };
        })
        .filter(Boolean);

      // Carry-forward files (contract, crews, additional_services,
      // operator-name overrides). Tripsheet URL is intentionally
      // dropped — stale and regenerable.
      const srcFiles = source.files || {};
      const files = {};
      if (srcFiles.contract) files.contract = srcFiles.contract;
      if (srcFiles.crews) files.crews = srcFiles.crews;
      if (srcFiles.additional_services) files.additional_services = srcFiles.additional_services;
      if (srcFiles.operator_name_overrides && typeof srcFiles.operator_name_overrides === 'object') {
        files.operator_name_overrides = { ...srcFiles.operator_name_overrides };
      }

      // Carry-forward passengers (minus the source ids).
      const sourcePassengers = Array.isArray(source.passengers) ? source.passengers : [];
      const passengers = sourcePassengers.map((p) => {
        // eslint-disable-next-line no-unused-vars
        const { quote_passenger_id, quote_id, created_at, updated_at, ...rest } = p;
        return rest;
      });

      // Build the quote payload. quote_number omitted — BE assigns it.
      const quote = {
        broker_org_id: userProfile.org_id,
        created_by: userProfile.userprofile_id,
        client_id: source.client_id || source.client?.client_id || null,
        departure_date: source.departure_date,
        return_date: source.return_date || null,
        passenger_count: source.passenger_count,
        departure_airport: source.departure_airport,
        arrival_airport: source.arrival_airport,
        is_round_trip: !!source.is_round_trip,
        is_multi_leg: !!source.is_multi_leg,
        note_to_client: source.note_to_client || null,
        status: 'DRAFT',
        expires_at: validUntil,
        passengers,
        ...(Object.keys(files).length > 0 ? { files } : {}),
      };

      // One atomic call — quote + all bids + all bid_options + passengers
      // + files in one transaction.
      const newQuote = await apiClient.createQuoteWithBids({
        quote,
        bids,
      });
      if (!newQuote?.quote_id) {
        throw new Error('createQuoteWithBids response missing quote_id');
      }

      // Seed drawer + list caches off the fresh server truth.
      queryClient.setQueryData(['quoteDetails', newQuote.quote_id], newQuote);
      queryClient.invalidateQueries({ queryKey: ['quotes'] });

      return { success: true, data: newQuote };
    } catch (err) {
      console.error('duplicateQuote failed:', err);
      toast.error(err?.message || 'Failed to duplicate quote', { position: 'bottom-right' });
      return { success: false, error: err };
    } finally {
      setIsDuplicating(false);
      setCurrentStep(null);
    }
  }, [queryClient, userProfile]);

  return { duplicateQuote, isDuplicating, currentStep };
}
